import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Settings, MessageSquare, Send, X as XIcon, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

function parseNotes(raw) {
  if (!raw) return defaultForm();
  try { const p = JSON.parse(raw); if (p._v===1) return p; } catch {}
  return { ...defaultForm(), notas_internas: raw };
}
function defaultForm() {
  return { _v:1,
    queixa:       { motivo:"", queixa_principal:"", historia_atual:"" },
    procedimento: { descricao:"", observacoes:"", produto:"" },
    produto:      { nome:"", lote:"", validade:"", quantidade:"", pontos:"" },
    orientacoes:  { instrucoes:"", restricoes:"" },
    retorno:      { data_recomendada:"", notas:"" },
    notas_internas:"",
  };
}
const elapsedFmt = (iso) => {
  if (!iso) return "00:00:00";
  const d = Math.max(0, Math.floor((Date.now()-new Date(iso).getTime())/1000));
  return [Math.floor(d/3600),Math.floor((d%3600)/60),d%60].map(n=>String(n).padStart(2,"0")).join(":");
};
const recFmt = (s) => [Math.floor(s/3600),Math.floor((s%3600)/60),s%60].map(n=>String(n).padStart(2,"0")).join(":");
const dateFmt = (iso) => iso ? new Date(iso+"T12:00:00").toLocaleDateString("pt-BR") : "";

const STATUS_LABEL = { pending:"Aguardando", confirmed:"Confirmado", in_progress:"Em atendimento", completed:"Concluído" };
const STATUS_STYLES = {
  in_progress:{ color:"#c96d00", background:"#ffefc5" },
  confirmed:  { color:"#276ef1", background:"#e5efff" },
  pending:    { color:"#555",    background:"#f0f1f3" },
  completed:  { color:"#10865d",background:"#d4f5e5" },
};

const TABS = [
  {key:"queixa",label:"Queixa"},{key:"transcricao",label:"Transcrição"},
  {key:"procedimento",label:"Procedimento"},{key:"produto",label:"Produto"},
  {key:"orientacoes",label:"Orientações"},{key:"retorno",label:"Retorno"},
  {key:"notas",label:"Notas internas"},
];

const INP = { display:"block",width:"100%",padding:"10px 12px",border:"1px solid #d8dce2",
  borderRadius:8,color:"#4e5662",background:"#fff",outline:"none",font:"14px/1.45 inherit",resize:"vertical" };


/* ── Componentes de chat inline (usados dentro do AtendimentoPanel) ── */
function avatarColor(name="") {
  const COLS=[["#7c3aed","#f0e8ff"],["#0369a1","#e0f2fe"],["#b45309","#fef3c7"],["#065f46","#d1fae5"],["#9d174d","#fce7f3"]];
  return COLS[name.charCodeAt(0)%COLS.length]||COLS[0];
}
function initials(name="") { return name.split(" ").slice(0,2).map(w=>w[0]||"").join("").toUpperCase()||"?"; }
function timeFmtChat(iso) { try{return new Date(iso).toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"});}catch{return "";} }

function ChatUnreadBadge() {
  const [count, setCount] = useState(0);
  const lastSeen = useRef(new Date().toISOString());
  useEffect(()=>{
    const fetch = async()=>{
      try{const{data}=await api.get("/admin/chat?limit=20");
        const n=data.filter(m=>m.created_at>lastSeen.current).length;
        if(n>0)setCount(c=>c+n);lastSeen.current=new Date().toISOString();}catch{}
    };
    const t=setInterval(fetch,8000);return()=>clearInterval(t);
  },[]);
  if(!count)return null;
  return <span style={{position:"absolute",top:-4,right:-4,minWidth:18,height:18,borderRadius:9,
    background:"#ef4444",color:"#fff",fontSize:10,fontWeight:700,
    display:"flex",alignItems:"center",justifyContent:"center",padding:"0 3px"}}>{count>9?"9+":count}</span>;
}

function InlineChat({onClose,user}) {
  const [msgs,setMsgs]     = useState([]);
  const [text,setText]     = useState("");
  const [sending,setSending]= useState(false);
  const bottomRef           = useRef(null);
  const inputRef            = useRef(null);

  const fetch = async()=>{ try{const{data}=await api.get("/admin/chat?limit=60");setMsgs(data);}catch{} };
  useEffect(()=>{fetch();setTimeout(()=>{bottomRef.current?.scrollIntoView({behavior:"smooth"});inputRef.current?.focus();},80);},[]);// eslint-disable-line
  useEffect(()=>{const t=setInterval(fetch,7000);return()=>clearInterval(t);},[]);// eslint-disable-line
  useEffect(()=>{bottomRef.current?.scrollIntoView({behavior:"smooth"});},[msgs]);

  const send=async()=>{
    const t=text.trim();if(!t)return;
    setSending(true);setText("");
    try{await api.post("/admin/chat",{text:t});await fetch();}catch{}finally{setSending(false);}
  };

  const grouped=msgs.reduce((acc,m)=>{
    const last=acc[acc.length-1];
    if(last&&last[0].user_id===m.user_id&&(new Date(m.created_at)-new Date(last[last.length-1].created_at))<120000)last.push(m);
    else acc.push([m]);return acc;
  },[]);

  return (
    <div style={{position:"absolute",bottom:"100%",left:"50%",transform:"translateX(-50%)",
      width:360,maxHeight:480,background:"#fff",borderRadius:16,
      boxShadow:"0 -4px 32px rgba(0,0,0,0.15)",border:"1px solid #e5e7eb",
      display:"flex",flexDirection:"column",zIndex:90,
      animation:"chatSlideUp .15s ease-out",marginBottom:8}}>
      {/* header */}
      <div style={{background:"#44403c",color:"#fff",padding:"12px 16px",borderRadius:"16px 16px 0 0",
        display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
        <Users size={16}/>
        <span style={{fontWeight:700,fontSize:14,flex:1}}>Chat da equipe</span>
        <button onClick={onClose} style={{background:"rgba(255,255,255,0.15)",border:0,borderRadius:"50%",
          width:28,height:28,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",cursor:"pointer"}}>
          <XIcon size={14}/>
        </button>
      </div>
      {/* msgs */}
      <div style={{flex:1,overflowY:"auto",padding:"12px 14px",background:"#f9fafb",display:"flex",flexDirection:"column",gap:3}}>
        {msgs.length===0&&<p style={{color:"#9ca3af",textAlign:"center",fontStyle:"italic",marginTop:20,fontSize:13}}>Nenhuma mensagem ainda</p>}
        {grouped.map((g,gi)=>{
          const mine=g[0].user_id===user?.id;
          const [fg,bg]=avatarColor(g[0].user_name);
          return <div key={gi} style={{display:"flex",flexDirection:"column",alignItems:mine?"flex-end":"flex-start",gap:2,marginTop:6}}>
            {!mine&&<div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
              <div style={{width:24,height:24,borderRadius:"50%",background:bg,color:fg,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:9,flexShrink:0}}>{initials(g[0].user_name)}</div>
              <span style={{fontSize:11,fontWeight:600,color:"#374151"}}>{g[0].user_name}</span>
            </div>}
            <div style={{display:"flex",flexDirection:"column",alignItems:mine?"flex-end":"flex-start",gap:2,marginLeft:mine?0:30}}>
              {g.map((m,mi)=><div key={m.id}>
                <div style={{maxWidth:220,padding:"7px 11px",fontSize:13,lineHeight:1.4,borderRadius:16,
                  ...(mine?{background:"#44403c",color:"#fff",borderBottomRightRadius:4}:{background:"#fff",color:"#111827",border:"1px solid #e5e7eb",borderBottomLeftRadius:4})}}>
                  {m.text}
                </div>
                {mi===g.length-1&&<div style={{fontSize:10,color:"#9ca3af",marginTop:1,textAlign:mine?"right":"left",padding:"0 3px"}}>{timeFmtChat(m.created_at)}</div>}
              </div>)}
            </div>
          </div>;
        })}
        <div ref={bottomRef}/>
      </div>
      {/* input */}
      <div style={{padding:"8px 12px",borderTop:"1px solid #e5e7eb",background:"#fff",display:"flex",alignItems:"flex-end",gap:8,borderRadius:"0 0 16px 16px",flexShrink:0}}>
        <textarea ref={inputRef} value={text} onChange={e=>setText(e.target.value)}
          onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}}
          placeholder="Mensagem…" rows={1}
          style={{flex:1,padding:"8px 12px",borderRadius:16,border:"1.5px solid #e5e7eb",outline:"none",resize:"none",font:"13px/1.4 inherit",maxHeight:72,boxSizing:"border-box"}}/>
        <button onClick={send} disabled={sending||!text.trim()}
          style={{width:34,height:34,borderRadius:"50%",border:0,
            background:text.trim()?"#44403c":"#e5e7eb",color:text.trim()?"#fff":"#9ca3af",
            display:"flex",alignItems:"center",justifyContent:"center",cursor:text.trim()?"pointer":"default",flexShrink:0}}>
          <Send size={14}/>
        </button>
      </div>
    </div>
  );
}

export default function AtendimentoPanel({ appt, board, onClose, onComplete, onNext }) {
  const { user } = useAuth();
  const [stockItems, setStockItems]       = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [productOptions, setProductOptions] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [fefoMap, setFefoMap]             = useState({});
  const LS_KEY = `atendimento_${appt.id}`;

  /* state */
  const [startedAt, setStartedAt] = useState(()=>{
    try { const c=JSON.parse(localStorage.getItem(LS_KEY)||"{}"); if(c.startedAt) return c.startedAt; } catch {}
    return appt.started_at||null;
  });
  const [, forceRender]           = useState(0);
  const [tab, setTab]             = useState(appt.started_at ? "transcricao" : "queixa");
  const [showStart, setShowStart] = useState(!appt.started_at);
  const [starting, setStarting]   = useState(false);
  const [form, setForm]           = useState(()=>{
    try { const c=JSON.parse(localStorage.getItem(LS_KEY)||"{}"); if(c.form) return c.form; } catch {}
    return parseNotes(appt.notes);
  });
  const setField = (s,f,v) => setForm(p=>({...p,[s]:{...p[s],[f]:v}}));
  const [saving, setSaving]             = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);
  const [checklist, setChecklist]       = useState({evolucao:false,procedimento:false,orientacoes:false,retorno:false,pagamento:false});
  const [history, setHistory]           = useState([]);
  const [offline, setOffline]           = useState(!navigator.onLine);
  const [pendingSync, setPendingSync]   = useState(false);
  const [transcript, setTranscript]     = useState(()=>{
    try { const c=JSON.parse(localStorage.getItem(LS_KEY)||"{}"); return c.transcript||[]; } catch { return []; }
  });
  const [recording, setRecording]       = useState(false);
  const [paused, setPaused]             = useState(false);
  const [speaker, setSpeaker]           = useState("Profissional");
  const [recSeconds, setRecSeconds]     = useState(0);
  const [summary, setSummary]           = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryEdits, setSummaryEdits] = useState({});
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [chatOpen, setChatOpen]           = useState(false);
  const [openaiKey, setOpenaiKey]       = useState(()=>localStorage.getItem("openai_key")||"");
  const recogRef        = useRef(null);
  const recTimerRef     = useRef(null);
  const transcriptRef   = useRef(null);
  const shouldRestartRef = useRef(false);  // controla se onend deve reiniciar
  const speakerRef      = useRef("Profissional"); // evita stale closure no onresult

  /* effects */
  useEffect(() => {
    if (!startedAt) return;
    const t = setInterval(() => forceRender(n=>n+1), 1000);
    return () => clearInterval(t);
  }, [startedAt]);

  useEffect(() => {
    const onOffline = () => { setOffline(true);  toast.warning("Sem conexão — dados salvos localmente"); };
    const onOnline  = () => { setOffline(false); setPendingSync(true); toast.success("Conexão restaurada"); };
    window.addEventListener("offline", onOffline);
    window.addEventListener("online",  onOnline);
    return () => { window.removeEventListener("offline", onOffline); window.removeEventListener("online", onOnline); };
  }, []);

  useEffect(() => {
    try { localStorage.setItem(LS_KEY, JSON.stringify({form,transcript,startedAt,savedAt:new Date().toISOString()})); }
    catch {}
  }, [form, transcript, startedAt]); // eslint-disable-line

  useEffect(() => {
    const t = setInterval(() => {
      try { localStorage.setItem(LS_KEY, JSON.stringify({form,transcript,startedAt,savedAt:new Date().toISOString()})); }
      catch {}
    }, 10000);
    return () => clearInterval(t);
  }, [form, transcript, startedAt]); // eslint-disable-line

  useEffect(() => {
    if (!pendingSync || offline) return;
    setPendingSync(false);
    api.put(`/admin/appointments/${appt.id}`,{notes:JSON.stringify(form)})
      .then(()=>toast.success("Sincronizado com o servidor")).catch(()=>{});
  }, [pendingSync, offline]); // eslint-disable-line

  useEffect(()=>{
    api.get("/admin/appointments").then(r=>{
      const all=r.data?.items||r.data||[];
      setHistory(all.filter(a=>a.id!==appt.id&&(a.patient_phone===appt.patient_phone||a.patient_email===appt.patient_email))
        .sort((a,b)=>`${b.date}${b.time}`.localeCompare(`${a.date}${a.time}`)).slice(0,8));
    }).catch(()=>{});
  },[appt.id]); // eslint-disable-line

  const queue = useMemo(()=>{
    if (!board) return [];
    return ["pending","confirmed","in_progress"].flatMap(c=>board.columns?.[c]||[])
      .sort((a,b)=>(a.time||"").localeCompare(b.time||""));
  },[board]);

  /* métodos */
  const saveKey = async () => {
    localStorage.setItem("openai_key", openaiKey);
    try { await api.put("/admin/api-keys", { openai_api_key: openaiKey }); } catch {}
    setShowKeyModal(false); toast.success("Chave salva");
  };

  const startRecording = () => {
    const SR = window.SpeechRecognition||window.webkitSpeechRecognition;
    if (!SR) { toast.error("Use Chrome ou Edge para transcrição de voz"); return; }
    const r = new SR(); r.lang="pt-BR"; r.continuous=true; r.interimResults=true;
    shouldRestartRef.current = true; // marca que deve reiniciar ao onend
    recogRef.current = r;
    r.onresult = e => {
      for (let i=e.resultIndex;i<e.results.length;i++) {
        if (e.results[i].isFinal) {
          const text=e.results[i][0].transcript.trim();
          if (text) {
            const now=new Date();
            const t=[now.getHours(),now.getMinutes(),now.getSeconds()].map(n=>String(n).padStart(2,"0")).join(":");
            // usa ref para evitar stale closure no speaker
            setTranscript(prev=>[...prev,{role:speakerRef.current,time:t,text}]);
            setTimeout(()=>transcriptRef.current?.scrollTo({top:9999,behavior:"smooth"}),50);
          }
        }
      }
    };
    r.onerror = e => { if (e.error!=="no-speech") toast.error("Microfone: "+e.error); };
    // usa ref — nunca captura valor stale de paused/stopped
    r.onend = () => { if (shouldRestartRef.current) r.start(); };
    r.start(); setRecording(true); setPaused(false);
    recTimerRef.current = setInterval(()=>setRecSeconds(s=>s+1),1000);
  };
  const pauseRecording  = () => {
    shouldRestartRef.current = false; // impede onend de reiniciar
    recogRef.current?.stop();
    clearInterval(recTimerRef.current);
    setPaused(true); setRecording(false);
  };
  const resumeRecording = () => startRecording();
  const stopRecording   = () => {
    shouldRestartRef.current = false; // impede onend de reiniciar
    recogRef.current?.stop();
    recogRef.current = null;
    clearInterval(recTimerRef.current);
    setRecording(false); setPaused(false);
  };

  const generateSummary = async () => {
    if (!transcript.length) { toast.error("Nenhuma transcrição"); return; }
    setSummaryLoading(true);
    try {
      const { data } = await api.post(`/admin/appointments/${appt.id}/summarize`,
        { transcript, openai_key: openaiKey||undefined });
      setSummary(data); setSummaryEdits({}); toast.success("Resumo gerado");
    } catch { toast.error("Falha — configure a chave OpenAI (ícone ⚙)"); }
    finally { setSummaryLoading(false); }
  };

  const fillFromSummary = () => {
    const s={...summary,...summaryEdits};
    setForm(f=>({...f,
      queixa:       {...f.queixa,queixa_principal:s.queixa||f.queixa.queixa_principal,historia_atual:s.historico||f.queixa.historia_atual},
      procedimento: {...f.procedimento,observacoes:s.avaliacao||f.procedimento.observacoes,descricao:s.conduta||f.procedimento.descricao},
      retorno:      {...f.retorno,notas:s.retorno||f.retorno.notas},
    }));
    toast.success("Resumo aplicado — revise antes de salvar"); setTab("queixa");
  };

  useEffect(()=>{
    // Se a consulta já foi iniciada (vindo do Kanban), inicia gravação automaticamente
    if (appt.started_at) {
      setShowStart(false);
      setTimeout(() => startRecording(), 600);
    }
    return ()=>stopRecording();
  },[]);// eslint-disable-line

  const comecar = async () => {
    setStarting(true);
    try {
      await api.put(`/admin/appointments/${appt.id}`,{status:"in_progress"});
      const r = await api.get(`/admin/appointments/${appt.id}`).then(r=>r.data).catch(()=>null);
      setStartedAt(r?.started_at||new Date().toISOString());
    } catch {} finally { setStarting(false); }
    setShowStart(false);
    setTab("transcricao");
    setTimeout(() => startRecording(), 300);
  };

  const save = async (quiet=false) => {
    // Sempre salva localmente primeiro
    try { localStorage.setItem(LS_KEY, JSON.stringify({ form, transcript, startedAt, savedAt: new Date().toISOString() })); } catch {}
    if (offline) { if (!quiet) toast.warning("Salvo localmente — sincronizará quando a internet voltar"); setSaving(false); return; }
    setSaving(true);
    try {
      await api.put(`/admin/appointments/${appt.id}`,{notes:JSON.stringify(form)});
      if (!quiet) toast.success("Anotações salvas");
    } catch { if (!quiet) toast.warning("Falha na rede — salvo localmente"); } finally { setSaving(false); }
  };

  const searchProducts = async (q) => {
    setProductSearch(q);
    if (!q || q.length < 2) { setProductOptions([]); return; }
    setLoadingProducts(true);
    try {
      const r = await api.get(`/admin/stock/products?q=${encodeURIComponent(q)}&status=active`);
      setProductOptions(r.data || []);
    } catch { setProductOptions([]); }
    finally { setLoadingProducts(false); }
  };

  const fetchFefo = async (productId) => {
    if (fefoMap[productId]) return fefoMap[productId];
    try {
      const r = await api.get(`/admin/stock/fefo?product_id=${productId}`);
      const batches = r.data || [];
      setFefoMap(prev => ({ ...prev, [productId]: batches }));
      return batches;
    } catch { return []; }
  };

  const addStockItem = async (product) => {
    setProductSearch(""); setProductOptions([]);
    const batches = await fetchFefo(product.id);
    const s = batches[0] || null;
    setStockItems(prev => [...prev, {
      product_id: product.id,
      product_name: product.name,
      unit_consumption: product.unit_consumption || product.unit_entry || "un",
      batch_id: s ? s.id : null,
      batch_number: s ? s.batch_number : "",
      expiry_date: s ? s.expiry_date : "",
      qty_available: s != null ? s.qty_available : null,
      quantity: 1,
      notes: "",
    }]);
  };

  const removeStockItem = (idx) => setStockItems(prev => prev.filter((_, i) => i !== idx));
  const updateStockItem = (idx, field, val) =>
    setStockItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: val } : it));

  const consumeStock = async () => {
    if (!stockItems.length) return;
    const items = stockItems
      .filter(it => it.product_id && Number(it.quantity) > 0)
      .map(it => ({
        product_id: it.product_id,
        batch_id: it.batch_id || undefined,
        quantity: Number(it.quantity),
        notes: it.notes || undefined,
      }));
    if (!items.length) return;
    try {
      await api.post("/admin/stock/consume", {
        appointment_id: appt.id,
        patient_id: appt.patient_id || appt.lead_id || undefined,
        professional_id: appt.professional_id || undefined,
        items,
        notes: "Atendimento: " + (appt.service_name || ""),
      });
    } catch (e) {
      toast.error("Erro ao registrar consumo de estoque");
      throw e;
    }
  };

  const conclude = async () => {
    await consumeStock();
    await save(true);
    try {
      const phone=(appt.patient_phone||"").replace(/\D/g,"");
      const email=appt.patient_email||"";
      const sp=phone?`phone=${phone}`:`email=${encodeURIComponent(email)}`;
      const lr=await api.get(`/admin/leads/search-by-contact?${sp}`).catch(()=>null);
      const lead=lr?.data;
      if (lead?.id) {
        const f=form; const lines=[];
        if (f.queixa?.queixa_principal) lines.push(`Queixa: ${f.queixa.queixa_principal}`);
        if (f.queixa?.historia_atual)   lines.push(`Histórico: ${f.queixa.historia_atual}`);
        if (f.procedimento?.descricao)  lines.push(`Procedimento: ${f.procedimento.descricao}`);
        if (f.produto?.nome)            lines.push(`Produto: ${f.produto.nome}`);
        if (f.orientacoes?.instrucoes)  lines.push(`Orientações: ${f.orientacoes.instrucoes}`);
        if (f.retorno?.data_recomendada) lines.push(`Retorno: ${f.retorno.data_recomendada} ${f.retorno.notas||""}`);
        if (lines.length)
          await api.post(`/admin/leads/${lead.id}/notes`,{
            content:lines.join("\n"), procedure:appt.service_name||"",
            author:user?.name||appt.professional_name||"Profissional",
          });
        toast.success("Prontuário salvo na ficha do paciente");
      }
    } catch {}
    try { localStorage.removeItem(LS_KEY); } catch {}
    onComplete(appt); onClose();
  };

  const wa=appt.patient_phone?.replace(/\D/g,"");
  const isFirst=history.length===0;
  const allChecked=Object.values(checklist).every(Boolean);

  /* estilos inline (replicando o protótipo) */
  const orange="#f59e0b", line="#e3e6eb", ink="#171b27", muted="#6d7482";

  const renderTab = () => {
    if (tab==="transcricao") return (
      <div style={{display:"grid",gridTemplateColumns:"minmax(390px,1fr) minmax(410px,1fr)",
        gap:16,padding:20,flex:1,overflow:"hidden",minHeight:0}}>

        {/* Transcript card */}
        <div style={{display:"flex",flexDirection:"column",background:"#fff",border:`1px solid ${line}`,borderRadius:10,overflow:"hidden"}}>
          <div style={{padding:"20px 22px",borderBottom:`1px solid ${line}`}}>
            <h2 style={{fontSize:18,margin:0}}>Transcrição em tempo real</h2>
            <div style={{display:"flex",alignItems:"center",gap:7,marginTop:7,fontWeight:600,
              color:recording?"#11aa64":paused?"#c47d00":"#8b919b"}}>
              <span style={{width:7,height:7,borderRadius:"50%",display:"inline-block",
                background:recording?"#16c77a":paused?"#f0a11b":"#8b919b",
                boxShadow:recording?"0 0 0 5px #e6fbf1":"none"}} />
              {recording?"Transcrevendo":paused?"Pausado":"Inativo"}
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:15,padding:"14px 22px",borderBottom:`1px solid ${line}`}}>
            <button onClick={()=>{ const ns=speaker==="Profissional"?"Paciente":"Profissional"; speakerRef.current=ns; setSpeaker(ns); }}
              style={{padding:"3px 10px",borderRadius:6,border:`1px solid ${line}`,cursor:"pointer",fontSize:12,fontWeight:650,
                background:speaker==="Profissional"?"#fff2d8":"#e5efff",color:speaker==="Profissional"?"#e17a00":"#276ef1"}}>
              {speaker}
            </button>
            <div style={{flex:1,height:24,borderRadius:4,
              background:recording?"repeating-linear-gradient(90deg,#16c77a 0 2px,transparent 2px 5px)":"#f0f1f3",
              WebkitMask:"linear-gradient(0deg,transparent 5%,#000 45% 55%,transparent 95%)",
              mask:"linear-gradient(0deg,transparent 5%,#000 45% 55%,transparent 95%)"}} />
            <time style={{fontVariantNumeric:"tabular-nums",fontSize:14}}>{recFmt(recSeconds)}</time>
            {!recording&&!paused&&<button onClick={startRecording} style={{width:38,height:38,borderRadius:"50%",border:`1px solid ${line}`,background:"#dbf9e9",color:"#089d5d",cursor:"pointer",fontWeight:800,fontSize:16}}>▶</button>}
            {recording&&<button onClick={pauseRecording} style={{width:38,height:38,borderRadius:"50%",border:`1px solid ${line}`,background:"#f5f6f7",cursor:"pointer",fontWeight:800}}>Ⅱ</button>}
            {paused&&<button onClick={resumeRecording} style={{width:38,height:38,borderRadius:"50%",border:`1px solid ${line}`,background:"#dbf9e9",color:"#089d5d",cursor:"pointer",fontWeight:800,fontSize:16}}>▶</button>}
            {(recording||paused)&&<button onClick={stopRecording} style={{width:38,height:38,borderRadius:"50%",border:"1px solid #ef8888",background:"#fff",color:"#e34d4d",cursor:"pointer",fontWeight:800}}>■</button>}
          </div>
          <div ref={transcriptRef} style={{flex:1,overflowY:"auto",padding:"12px 22px"}}>
            {transcript.length===0&&<p style={{color:muted,textAlign:"center",marginTop:32,fontStyle:"italic"}}>Clique em ▶ para iniciar</p>}
            {transcript.map((m,i)=>{
              const isDoc=m.role==="Profissional";
              return <article key={i} style={{padding:"10px 0"}}>
                <div style={{display:"flex",alignItems:"center",gap:14}}>
                  <span style={{borderRadius:5,padding:"3px 8px",fontSize:12,background:isDoc?"#fff2d8":"#f0f1f3",color:isDoc?"#e17a00":ink}}>
                    {isDoc?(appt.professional_name?.split(" ")[0]||"Profissional"):(appt.patient_name?.split(" ")[0]||"Paciente")}
                  </span>
                  <time style={{fontSize:12,color:"#7b8390"}}>{m.time}</time>
                </div>
                <p style={{margin:"7px 0 0",lineHeight:1.6}}>{m.text}</p>
              </article>;
            })}
          </div>
          <div style={{padding:"14px 22px",borderTop:`1px solid ${line}`,color:"#767d89",fontSize:12}}>
            ⓘ &nbsp; Revise o conteúdo antes de salvar no prontuário
          </div>
        </div>

        {/* Summary card */}
        <div style={{display:"flex",flexDirection:"column",background:"#fff",border:`1px solid ${line}`,borderRadius:10,overflow:"hidden"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,padding:"20px 22px",minHeight:86,borderBottom:`1px solid ${line}`}}>
            <h2 style={{fontSize:18,margin:0,flex:1}}>Resumo assistido</h2>
            {summary&&<span style={{display:"inline-flex",alignItems:"center",borderRadius:6,padding:"4px 7px",fontSize:11,fontWeight:700,color:"#d87600",background:"#fff1cf"}}>Sugestão — requer revisão</span>}
            <button onClick={generateSummary} title="Gerar resumo" style={{border:0,background:"transparent",fontSize:20,color:"#68707c",cursor:"pointer"}}>↻</button>
            <button onClick={()=>setShowKeyModal(true)} title="Chave OpenAI"
              style={{border:0,background:"transparent",color:"#68707c",cursor:"pointer",display:"flex",alignItems:"center",padding:4}}>
              <Settings size={16}/>
            </button>
          </div>
          <div style={{flex:1,overflowY:"auto",padding:"0 22px"}}>
            {summaryLoading&&<div style={{textAlign:"center",padding:40}}>
              <div style={{width:32,height:32,border:"2px solid #f59e0b",borderTopColor:"transparent",borderRadius:"50%",animation:"spin .7s linear infinite",margin:"0 auto 12px"}}/>
              <p style={{color:muted}}>Gerando resumo com IA…</p>
            </div>}
            {!summaryLoading&&!summary&&<p style={{color:muted,textAlign:"center",marginTop:40,fontStyle:"italic",padding:"0 16px"}}>
              Transcreva a consulta e clique em ↻ para gerar o resumo com IA
            </p>}
            {!summaryLoading&&summary&&<div style={{display:"flex",flexDirection:"column",gap:12,padding:"16px 0"}}>
              {[{key:"queixa",label:"Queixa principal"},{key:"historico",label:"Histórico relatado"},
                {key:"avaliacao",label:"Avaliação"},{key:"conduta",label:"Conduta sugerida"},{key:"retorno",label:"Retorno"}]
                .map(({key,label})=>(
                  <label key={key}>
                    <div style={{display:"flex",justifyContent:"space-between",fontWeight:650,marginBottom:7,fontSize:14}}><span>{label}</span></div>
                    <textarea rows={3} value={summaryEdits[key]!==undefined?summaryEdits[key]:(summary[key]||"")}
                      onChange={e=>setSummaryEdits(ed=>({...ed,[key]:e.target.value}))} style={{...INP,minHeight:64}}/>
                  </label>
                ))}
            </div>}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1.35fr",gap:10,padding:"16px 22px",borderTop:`1px solid ${line}`}}>
            <button onClick={generateSummary} disabled={summaryLoading||!summary}
              style={{minHeight:46,padding:"0 20px",borderRadius:8,fontWeight:650,cursor:"pointer",border:`1px solid #d7dbe1`,background:"#fff",color:"#242a35",fontSize:14}}>
              ▤ &nbsp; Revisar resumo
            </button>
            <button onClick={fillFromSummary} disabled={!summary}
              style={{minHeight:46,padding:"0 20px",borderRadius:8,fontWeight:650,cursor:"pointer",border:`1px solid ${orange}`,background:orange,color:"#1e1e1e",fontSize:14}}>
              ✎ &nbsp; Preencher prontuário
            </button>
          </div>
        </div>
      </div>
    );

    return (
      <div style={{padding:20,flex:1,overflowY:"auto"}}>
        <div style={{maxWidth:680,margin:"0 auto",background:"#fff",border:`1px solid ${line}`,borderRadius:10,padding:24}}>
          {tab==="queixa"&&<>
            <h3 style={{margin:"0 0 20px",fontSize:16}}>Queixa principal</h3>
            {[["motivo","Motivo da consulta","text"],["queixa_principal","Queixa principal","textarea"],["historia_atual","História atual","textarea"]]
              .map(([f,l,t])=>(
              <label key={f} style={{display:"flex",flexDirection:"column",gap:6,marginBottom:16}}>
                <span style={{fontSize:12,fontWeight:650,color:muted}}>{l}</span>
                {t==="textarea"?<textarea rows={3} value={form.queixa[f]} onChange={e=>setField("queixa",f,e.target.value)} style={{...INP,minHeight:72}}/>
                  :<input value={form.queixa[f]} onChange={e=>setField("queixa",f,e.target.value)} style={INP}/>}
              </label>
            ))}
          </>}
          {tab==="procedimento"&&<>
            <h3 style={{margin:"0 0 20px",fontSize:16}}>Procedimento</h3>
            {[["descricao","Descrição","textarea"],["produto","Produto utilizado","text"],["observacoes","Observações clínicas","textarea"]]
              .map(([f,l,t])=>(
              <label key={f} style={{display:"flex",flexDirection:"column",gap:6,marginBottom:16}}>
                <span style={{fontSize:12,fontWeight:650,color:muted}}>{l}</span>
                {t==="textarea"?<textarea rows={3} value={form.procedimento[f]} onChange={e=>setField("procedimento",f,e.target.value)} style={{...INP,minHeight:72}}/>
                  :<input value={form.procedimento[f]} onChange={e=>setField("procedimento",f,e.target.value)} style={INP}/>}
              </label>
            ))}
          </>}
          {tab==="produto"&&<>
            <h3 style={{margin:"0 0 6px",fontSize:16}}>Produtos utilizados</h3>
            <p style={{margin:"0 0 16px",fontSize:12,color:muted}}>Selecione os itens consumidos; lote FEFO sugerido automaticamente.</p>
            <div style={{position:"relative",marginBottom:16}}>
              <input
                placeholder="Buscar produto no estoque..."
                value={productSearch}
                onChange={e=>searchProducts(e.target.value)}
                style={{...INP,paddingRight:32}}
              />
              {loadingProducts&&<span style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",color:muted,fontSize:12}}>...</span>}
              {productOptions.length>0&&(
                <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,background:"#fff",border:"1px solid #e3e6eb",borderRadius:8,boxShadow:"0 4px 16px rgba(0,0,0,.08)",zIndex:50,maxHeight:200,overflowY:"auto"}}>
                  {productOptions.map(p=>(
                    <button key={p.id} onClick={()=>addStockItem(p)}
                      style={{display:"block",width:"100%",textAlign:"left",padding:"10px 14px",background:"none",border:0,cursor:"pointer",fontSize:13,borderBottom:"1px solid #f0f0f0"}}>
                      <strong>{p.name}</strong>
                      {p.sku&&<span style={{color:muted,marginLeft:6,fontSize:11}}>#{p.sku}</span>}
                      <span style={{float:"right",color:(p.qty_available||0)>0?"#16c77a":"#e34d4d",fontSize:11}}>
                        {p.qty_available!=null?p.qty_available:"-"} {p.unit_consumption||p.unit_entry}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {stockItems.length===0&&(
              <div style={{padding:"28px 0",textAlign:"center",color:muted,fontSize:13,border:"1px dashed #d0d5dd",borderRadius:8}}>
                Nenhum produto adicionado
              </div>
            )}
            {stockItems.map((it,idx)=>(
              <div key={idx} style={{border:"1px solid #e3e6eb",borderRadius:10,padding:"14px 16px",marginBottom:12,background:"#fff"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                  <div>
                    <strong style={{fontSize:14}}>{it.product_name}</strong>
                    {it.batch_number&&(
                      <div style={{fontSize:11,color:muted,marginTop:3}}>
                        Lote: {it.batch_number}
                        {it.expiry_date&&<span> &middot; Val: {it.expiry_date}</span>}
                        {it.qty_available!=null&&<span> &middot; Disp: {it.qty_available} {it.unit_consumption}</span>}
                      </div>
                    )}
                  </div>
                  <button onClick={()=>removeStockItem(idx)} style={{background:"none",border:0,cursor:"pointer",color:"#e34d4d",fontSize:20,lineHeight:1,padding:0}}>&times;</button>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"120px 1fr",gap:10}}>
                  <label style={{display:"flex",flexDirection:"column",gap:4}}>
                    <span style={{fontSize:11,fontWeight:650,color:muted}}>Qtd ({it.unit_consumption})</span>
                    <input type="number" min="0.01" step="0.01"
                      value={it.quantity}
                      onChange={e=>updateStockItem(idx,"quantity",e.target.value)}
                      style={INP}/>
                  </label>
                  <label style={{display:"flex",flexDirection:"column",gap:4}}>
                    <span style={{fontSize:11,fontWeight:650,color:muted}}>Obs. (opcional)</span>
                    <input value={it.notes} onChange={e=>updateStockItem(idx,"notes",e.target.value)} style={INP} placeholder="p.ex. regiao frontal"/>
                  </label>
                </div>
              </div>
            ))}
          </>}
          {tab==="orientacoes"&&<>
            <h3 style={{margin:"0 0 20px",fontSize:16}}>Orientações pós-procedimento</h3>
            {[["instrucoes","Instruções","textarea"],["restricoes","Restrições","textarea"]]
              .map(([f,l,t])=>(
              <label key={f} style={{display:"flex",flexDirection:"column",gap:6,marginBottom:16}}>
                <span style={{fontSize:12,fontWeight:650,color:muted}}>{l}</span>
                <textarea rows={4} value={form.orientacoes[f]} onChange={e=>setField("orientacoes",f,e.target.value)} style={{...INP,minHeight:96}}/>
              </label>
            ))}
          </>}
          {tab==="retorno"&&<>
            <h3 style={{margin:"0 0 20px",fontSize:16}}>Retorno</h3>
            <label style={{display:"flex",flexDirection:"column",gap:6,marginBottom:16}}>
              <span style={{fontSize:12,fontWeight:650,color:muted}}>Data recomendada</span>
              <input type="date" value={form.retorno.data_recomendada} onChange={e=>setField("retorno","data_recomendada",e.target.value)} style={INP}/>
            </label>
            <label style={{display:"flex",flexDirection:"column",gap:6,marginBottom:16}}>
              <span style={{fontSize:12,fontWeight:650,color:muted}}>Observações</span>
              <textarea rows={4} value={form.retorno.notas} onChange={e=>setField("retorno","notas",e.target.value)} style={{...INP,minHeight:96}}/>
            </label>
          </>}
          {tab==="notas"&&<>
            <h3 style={{margin:"0 0 8px",fontSize:16}}>Notas internas</h3>
            <p style={{margin:"0 0 16px",fontSize:12,color:muted}}>Visível apenas para a equipe.</p>
            <textarea rows={10} value={form.notas_internas} onChange={e=>setForm(f=>({...f,notas_internas:e.target.value}))} style={{...INP,minHeight:200}}/>
          </>}
        </div>
      </div>
    );
  };

  return (
    <div style={{display:"grid",gridTemplateColumns:"250px minmax(0,1fr) 280px",gridTemplateRows:"94px 1fr 74px",
      minHeight:"100vh",fontFamily:"Inter,ui-sans-serif,system-ui,sans-serif",fontSize:14,color:ink,background:"#fafbfc"}}>

      {/* HEADER */}
      <header style={{gridColumn:"1/-1",display:"flex",alignItems:"center",justifyContent:"space-between",
        padding:"0 22px",borderBottom:`1px solid ${line}`,background:"#fff"}}>
        <div style={{display:"flex",alignItems:"center"}}>
          <div style={{width:54,height:54,borderRadius:"50%",background:"#fff2cf",color:"#e98500",fontSize:20,
            display:"grid",placeItems:"center",marginRight:14,fontWeight:700,flexShrink:0}}>
            {(appt.patient_name||"?").slice(0,2).toUpperCase()}
          </div>
          <div>
            <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
              <h1 style={{fontSize:20,margin:0}}>{appt.patient_name}</h1>
              {isFirst&&<span style={{display:"inline-flex",alignItems:"center",borderRadius:6,padding:"4px 7px",fontSize:11,fontWeight:700,color:"#7347e8",background:"#f0e8ff"}}>1ª consulta</span>}
            </div>
            <p style={{color:muted,margin:"2px 0 0",fontSize:13}}>
              {appt.service_name}
              {" "}<span style={{display:"inline-block",width:3,height:3,borderRadius:"50%",background:"#aeb3bd",margin:"0 8px 3px",verticalAlign:"middle"}}/>
              {appt.professional_name}
              {" "}<span style={{display:"inline-block",width:3,height:3,borderRadius:"50%",background:"#aeb3bd",margin:"0 8px 3px",verticalAlign:"middle"}}/>
              {appt.time}
            </p>
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",marginRight:18}}>
            <span style={{fontSize:10,letterSpacing:"0.1em",color:"#858c99"}}>DURAÇÃO</span>
            <strong style={{fontFamily:"ui-monospace,SFMono-Regular,Menlo,monospace",fontSize:22,color:"#e27900",letterSpacing:"0.06em"}}>
              {elapsedFmt(startedAt)}
            </strong>
            {offline && <span style={{fontSize:10,background:"#fef2f2",color:"#dc2626",padding:"2px 6px",borderRadius:4,marginTop:2,display:"block"}}>● SEM REDE</span>}
          </div>
          {wa&&<a href={`https://wa.me/55${wa}`} target="_blank" rel="noreferrer"
            style={{width:44,height:44,borderRadius:"50%",background:"#dbf9e9",color:"#089d5d",border:0,display:"grid",placeItems:"center",textDecoration:"none"}}>
            <MessageCircle size={20}/>
          </a>}
          <button onClick={onClose} style={{width:44,height:44,borderRadius:"50%",background:"#f5f6f7",border:0,color:"#59606b",fontSize:24,cursor:"pointer",display:"grid",placeItems:"center"}}>×</button>
        </div>
      </header>

      {/* SIDEBAR ESQUERDA */}
      <aside style={{background:"#fff",borderRight:`1px solid ${line}`,overflowY:"auto"}}>
        <h2 style={{fontSize:10,letterSpacing:"0.1em",color:"#858c99",height:54,margin:0,padding:"0 22px",display:"flex",alignItems:"center",borderBottom:`1px solid ${line}`}}>HISTÓRICO</h2>
        <div style={{padding:20}}>
          <article style={{display:"flex",flexDirection:"column",gap:7,padding:16,border:`1px solid ${orange}`,borderRadius:10,background:"linear-gradient(135deg,#fff 40%,#fff8e9)"}}>
            <strong style={{fontSize:13}}>♙ &nbsp; {isFirst?"Primeira consulta":"Consulta"}</strong>
            <span style={{color:muted,fontSize:12}}>{dateFmt(appt.date)} · {appt.time}</span>
            <span style={{color:muted,fontSize:12}}>{appt.professional_name}</span>
          </article>
          {history.map(h=>(
            <article key={h.id} style={{display:"flex",flexDirection:"column",gap:6,padding:"14px 16px",border:`1px solid ${line}`,borderRadius:10,marginTop:10,color:muted,fontSize:12}}>
              <strong style={{color:ink,fontSize:13}}>{h.service_name||"Consulta"}</strong>
              <span>{dateFmt(h.date)} · {h.time}</span>
              <span>{h.professional_name}</span>
            </article>
          ))}
          <hr style={{border:0,borderTop:`1px solid ${line}`,margin:"22px 0"}}/>
          <h3 style={{fontSize:13,margin:"0 0 10px"}}>Alertas clínicos</h3>
          <div style={{padding:"10px 12px",border:`1px solid ${line}`,borderRadius:9,marginBottom:8,background:"#fff",fontSize:13}}>♢ &nbsp; Sem alergias registradas</div>
          {isFirst&&<div style={{padding:"10px 12px",border:`1px solid ${line}`,borderRadius:9,background:"#fff",fontSize:13}}>ⓘ &nbsp; Primeiro atendimento</div>}
        </div>
      </aside>

      {/* WORKSPACE */}
      <main style={{minWidth:0,display:"flex",flexDirection:"column",overflow:"hidden"}}>
        <nav style={{height:54,display:"flex",alignItems:"stretch",borderBottom:`1px solid ${line}`,background:"#fff",overflowX:"auto",flexShrink:0}}>
          {TABS.map(t=>(
            <button key={t.key} onClick={()=>setTab(t.key)}
              style={{whiteSpace:"nowrap",padding:"0 15px",border:0,borderBottom:`2px solid ${tab===t.key?orange:"transparent"}`,
                background:"transparent",color:tab===t.key?"#e47700":"#5d6470",cursor:"pointer",fontSize:14}}>
              {t.label}
            </button>
          ))}
        </nav>
        {renderTab()}
      </main>

      {/* SIDEBAR DIREITA */}
      <aside style={{background:"#fff",borderLeft:`1px solid ${line}`,overflowY:"auto"}}>
        <h2 style={{fontSize:10,letterSpacing:"0.1em",color:"#858c99",height:54,margin:0,padding:"0 22px",display:"flex",alignItems:"center",borderBottom:`1px solid ${line}`}}>FILA DO DIA</h2>
        <div style={{padding:14}}>
          {queue.map(q=>{
            const isCur=q.id===appt.id;
            const ss=STATUS_STYLES[q.status]||STATUS_STYLES.pending;
            return <article key={q.id} style={{padding:14,border:`1px solid ${isCur?orange:line}`,borderRadius:10,marginBottom:12,background:isCur?"#fff8e9":"#fff"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
                <time style={{color:"#737b88",fontSize:13}}>{q.time}</time>
                <span style={{...ss,padding:"3px 7px",borderRadius:6,fontSize:11}}>{STATUS_LABEL[q.status]||q.status}</span>
              </div>
              <strong style={{display:"block",margin:"11px 0 3px",fontSize:14}}>{q.patient_name}</strong>
              <p style={{margin:0,color:"#818894",fontSize:12}}>{q.service_name}</p>
            </article>;
          })}
          {queue.length===0&&<p style={{color:muted,fontSize:12,textAlign:"center",marginTop:24}}>Fila vazia</p>}
        </div>
      </aside>

      {/* FOOTER */}
      <footer style={{gridColumn:"1/-1",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 22px",borderTop:`1px solid ${line}`,background:"#fff",position:"relative"}}>
        <button onClick={()=>save()} disabled={saving}
          style={{minHeight:46,padding:"0 20px",borderRadius:8,fontWeight:650,cursor:"pointer",border:`1px solid #d7dbe1`,background:"#fff",color:"#242a35",fontSize:14}}>
          ▣ &nbsp; Salvar rascunho
        </button>

        {/* Botão chat no centro do footer */}
        <button onClick={()=>setChatOpen(o=>!o)}
          style={{width:46,height:46,borderRadius:"50%",border:`1px solid #d7dbe1`,background:chatOpen?"#44403c":"#fff",
            color:chatOpen?"#fff":"#44403c",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",
            position:"relative",flexShrink:0}}>
          <Users size={20}/>
          {!chatOpen && <ChatUnreadBadge />}
        </button>

        <div style={{display:"flex",gap:16}}>
          <button onClick={()=>setShowChecklist(true)}
            style={{minHeight:46,padding:"0 20px",borderRadius:8,fontWeight:650,cursor:"pointer",border:`1px solid #d7dbe1`,background:"#fff",color:"#242a35",fontSize:14}}>
            ◉ &nbsp; Concluir atendimento
          </button>
          <button onClick={async()=>{await save(true);onNext&&onNext(appt);}}
            style={{minHeight:46,minWidth:170,padding:"0 20px",borderRadius:8,fontWeight:650,cursor:"pointer",border:`1px solid ${orange}`,background:orange,color:"#1e1e1e",fontSize:14}}>
            Próximo &nbsp; ›
          </button>
        </div>
      </footer>

      {/* CHAT INTERNO (popup acima do footer) */}
      {chatOpen && <InlineChat onClose={()=>setChatOpen(false)} user={user} />}

      {/* POPUP: INICIAR ATENDIMENTO */}
      {showStart&&(
        <div style={{position:"fixed",inset:0,zIndex:60,background:"rgba(0,0,0,0.45)",display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:"#fff",borderRadius:16,padding:"40px 48px",textAlign:"center",boxShadow:"0 20px 60px rgba(0,0,0,0.2)",maxWidth:360,width:"90%"}}>
            <div style={{width:56,height:56,borderRadius:"50%",background:"#fff2cf",display:"grid",placeItems:"center",margin:"0 auto 20px",fontSize:24}}>▶</div>
            <h2 style={{fontSize:22,margin:"0 0 8px"}}>Iniciar Atendimento</h2>
            <p style={{color:muted,margin:"0 0 28px",fontSize:14}}>{appt.patient_name} · {appt.service_name}</p>
            <button onClick={comecar} disabled={starting}
              style={{width:"100%",padding:"14px 0",borderRadius:10,border:0,background:"#16a34a",color:"#fff",fontSize:16,fontWeight:700,cursor:"pointer"}}>
              {starting?"Iniciando…":"Começar"}
            </button>
          </div>
        </div>
      )}

      {/* MODAL: CHECKLIST */}
      {showChecklist&&(
        <div style={{position:"fixed",inset:0,zIndex:60,background:"rgba(0,0,0,0.45)",display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:"#fff",borderRadius:16,padding:32,maxWidth:400,width:"90%",boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}}>
            <h2 style={{fontSize:18,margin:"0 0 20px"}}>Checklist de conclusão</h2>
            {[["evolucao","Evolução registrada"],["procedimento","Procedimento documentado"],
              ["orientacoes","Orientações fornecidas"],["retorno","Retorno agendado/orientado"],["pagamento","Pagamento confirmado"]]
              .map(([k,l])=>(
              <label key={k} style={{display:"flex",alignItems:"center",gap:12,marginBottom:14,cursor:"pointer",fontSize:14}}>
                <input type="checkbox" checked={checklist[k]} onChange={e=>setChecklist(c=>({...c,[k]:e.target.checked}))} style={{width:18,height:18,cursor:"pointer"}}/>
                <span style={{color:checklist[k]?ink:muted}}>{l}</span>
              </label>
            ))}
            <div style={{display:"flex",gap:12,marginTop:24}}>
              <button onClick={()=>setShowChecklist(false)} style={{flex:1,padding:"12px 0",borderRadius:8,border:`1px solid #d7dbe1`,background:"#fff",cursor:"pointer",fontWeight:650}}>Cancelar</button>
              <button onClick={conclude} disabled={!allChecked}
                style={{flex:1,padding:"12px 0",borderRadius:8,border:0,background:allChecked?"#16a34a":"#d1d5db",color:"#fff",cursor:allChecked?"pointer":"not-allowed",fontWeight:650}}>
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: CHAVE OPENAI */}
      {showKeyModal&&(
        <div style={{position:"fixed",inset:0,zIndex:60,background:"rgba(0,0,0,0.45)",display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:"#fff",borderRadius:16,padding:32,maxWidth:440,width:"90%",boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}}>
            <h2 style={{fontSize:18,margin:"0 0 8px"}}>Chave OpenAI</h2>
            <p style={{color:muted,fontSize:13,margin:"0 0 20px"}}>Usada para gerar o resumo assistido da transcrição. Fica salva no navegador e no servidor.</p>
            <input value={openaiKey} onChange={e=>setOpenaiKey(e.target.value)} placeholder="sk-..." type="password"
              style={{...INP,marginBottom:16,fontFamily:"monospace"}}/>
            <div style={{display:"flex",gap:12}}>
              <button onClick={()=>setShowKeyModal(false)} style={{flex:1,padding:"12px 0",borderRadius:8,border:`1px solid #d7dbe1`,background:"#fff",cursor:"pointer",fontWeight:650}}>Cancelar</button>
              <button onClick={saveKey} style={{flex:1,padding:"12px 0",borderRadius:8,border:0,background:orange,color:"#1e1e1e",cursor:"pointer",fontWeight:650}}>Salvar chave</button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
